<template>
  <AppSection
    id="basic-auth"
    class="mt-3"
    type="sub-section-1"
    :title="isOauth2 ? $getConst.pages.mail_credentials.mail_credentials_oauth_title : $getConst.pages.mail_credentials.mail_credentials_basic_auth_title"
    :is-section-loading="isLoading"
  >
    <b-row>

      <b-col v-if="isOauth2" cols="12" class="text-center">
        <a @click="loginSMTP" :class="{'disabled': isSigningInToSmtp}">
          <AppSvgIcon
            id="ms-button"
            type="button"
            class="mt-3 mt-sm-0 btn"
            :class="{'ms-button-active': false}"
            height="41"
            grow-by-height
            :icon="$getConst.svgIcons.microsoft_big"
          />
          <b-badge v-if="false" id="success-badge" variant="success" pill class="w-auto px-1"><i :class="$getConst.icons.check"/></b-badge>
        </a>
        <hr>
      </b-col>

      <template v-if="!isOauth2 || (isOauth2 && isOauth2Connected)">
        <b-col sm="6">
          <b-form-group  id="mail-host" :label="$t($getConst.pages.mail_credentials.mail_host_label) + ' *'" label-for="mail-host-input">
            <b-form-input
              id="mail-host-input"
              :disabled="hasWritePermission || isOauth2"
              v-model.trim="$v.mailCredentialsToEdit.mail_host.$model"
              :class="{ 'is-invalid': $v.mailCredentialsToEdit.mail_host.$error }"
              :placeholder="$t($getConst.pages.mail_credentials.mail_host_placeholder) + ' *'"
              size="lg"
              required
            ></b-form-input>
            <AppFormFeedback :errors="$v.mailCredentialsToEdit.mail_host"/>
          </b-form-group>
        </b-col>


        <b-col sm="6">
          <b-form-group  id="mail-port" :label="$t($getConst.pages.mail_credentials.mail_port_label) + ' *'" label-for="mail-port-input">
            <b-form-input
              id="mail-port-input"
              :disabled="hasWritePermission || isOauth2"
              v-model.trim="$v.mailCredentialsToEdit.mail_port.$model"
              :class="{ 'is-invalid': $v.mailCredentialsToEdit.mail_port.$error }"
              :placeholder="$t($getConst.pages.mail_credentials.mail_port_placeholder) + ' *'"
              size="lg"
              required
            ></b-form-input>
            <AppFormFeedback :errors="$v.mailCredentialsToEdit.mail_port"/>
          </b-form-group>
        </b-col>

        <b-col sm="6">
          <b-form-group  id="mail-username" :label="$t($getConst.pages.mail_credentials.mail_username_label) + ' *'" label-for="mail-username-input">
            <b-form-input
              id="mail-username-input"
              :disabled="hasWritePermission || isOauth2"
              v-model.trim="$v.mailCredentialsToEdit.mail_username.$model"
              :class="{ 'is-invalid': $v.mailCredentialsToEdit.mail_username.$error }"
              :placeholder="$t($getConst.pages.mail_credentials.mail_username_placeholder) + ' *'"
              size="lg"
              required
            ></b-form-input>
            <AppFormFeedback :errors="$v.mailCredentialsToEdit.mail_username"/>
          </b-form-group>
        </b-col>

        <b-col sm="6" v-if="!isOauth2">
          <b-form-group  id="mail-password" :label="$t($getConst.pages.mail_credentials.mail_password_label) + ' *'" label-for="mail-password"
          >
          </b-form-group>
          <b-input-group-append is-text id="toggle-hidden-password" class="toggle-icon">
            <a @click="onDisplayPassword">
              <i :class="this.passwordHidden === true ? this.$getConst.icons.key : this.$getConst.icons.keyOpen " aria-hidden="true"></i>
            </a>
          </b-input-group-append>

          <b-form-group  id="mail-password" class="password-right"
                         :class="this.passwordHidden === true ?  'passwordDisplayed': true">
            <b-input-group size="lg">
              <b-form-input
                id="mail-password-input"
                :disabled="hasWritePermission"
                :type="passwordInputType"
                v-model.trim="$v.mailCredentialsToEdit.mail_password.$model"
                :class="{ 'is-invalid': $v.mailCredentialsToEdit.mail_password.$error }"
                :placeholder="$t($getConst.pages.mail_credentials.mail_password_placeholder) + ' *'"
                size="lg"
                required
              ></b-form-input>
              <b-input-group-append is-text id="toggle-password">
                <a @click="onShowPassword">
                  <i :class="this.passwordInputType === 'password' ? this.$getConst.icons.eyeSlash : this.$getConst.icons.eye" aria-hidden="true"></i>
                </a>
              </b-input-group-append>
            </b-input-group>
            <AppFormFeedback :errors="$v.mailCredentialsToEdit.mail_password"/>
          </b-form-group>
        </b-col>
      </template>

    </b-row>

    <!--  Basic auth buttons  -->
    <AppConfirmButtons
      v-if="!hasWritePermission && !isOauth2"
      :second-button="hasAnyOfPermissions(
          [
            this.$getConst.permissions.readScope,
            this.$getConst.permissions.readScopeMailCredentials,
            this.$getConst.permissions.writeScope,
            this.$getConst.permissions.writeScopeMailCredentials,
          ]
        )"
      :second-button-text="this.$getConst.pages.mail_credentials.mail_test"
      :second-button-disabled="isTesting"
      @onSecondSubmit="testMailCredentials"
      @onSubmit="onSubmit"
      @onCancel="onCancel"
    />
  </AppSection>
</template>

<script>
  import {integer, required} from "vuelidate/lib/validators";
  import {testMailCredentials, updateMailCredentials, updateSMTPTokens} from "../../../../api/mailCredentials.api";
  import {snotifyConfig} from "../../../../shared/constants/snotify";
  import AppFormFeedback from "../../../../components/common/form/AppFormFeedback";
  import AppConfirmButtons from "../../../../components/common/utils/AppConfirmButtons";
  import AppSection from "../../../../components/common/sections/AppSection";

  export default {
    name: "MailCredentialsForm",
    components: {AppSection, AppConfirmButtons, AppFormFeedback},
    props: {
      mailCredentials: null,
      authType: null,
      scope_id: null
    },
    computed: {
      isOauth2() {
        return this.authType === "oauth2";
      }
    },
    data() {
      return {
        isLoading: false,
        isTesting: false,
        isSigningInToSmtp: false,
        mailCredentialsToEdit: {
          id: null,
          mail_host: null,
          mail_port: null,
          mail_username: null,
          mail_password: null,
          token: null,
          refresh_token :null
        },
        passwordInputType: "password",
        passwordHidden: true,
        hasWritePermission: !this.hasAnyOfPermissions(
          [this.$getConst.permissions.writeScopeNavCredentials, this.$getConst.permissions.writeScope]
        ),
        selectedDetails: null,
        isOauth2Connected: false
      }
    },
    validations: {
      mailCredentialsToEdit: {
        mail_host: {required},
        mail_port: {required, integer},
        mail_username: {required},
        mail_password: {},
      }
    },
    methods: {
      onSubmit(isOauth = false) {
        this.isLoading = true;
        this.$emit("isLoading", true);
        if (!isOauth) {
          this.mailCredentialsToEdit.refresh_token = null;
          this.mailCredentialsToEdit.token = null;
        }
        updateMailCredentials(this.scope_id, this.mailCredentialsToEdit).then(
          (response) => {
            this.isLoading = false;
            this.$emit("isLoading", false);
            this.$snotify.success(this.$t(this.$getConst.pages.alerts.success_operation));
            this.$emit("onSubmit", Object.assign({}, response.data));
          },
          (error) => {
            this.handleApiError(error);
            this.isLoading = false;
            this.$emit("isLoading", false);
          }
        )
      },
      testMailCredentials() {
        this.isTesting = true;
        this.$emit("isLoading", true);
        this.$snotify.async(
          this.$t(this.$getConst.pages.connectivity_test.smtp_request_sending),
          this.$t(this.$getConst.pages.alerts.please_wait),
          () => new Promise((resolve, reject) => {
            testMailCredentials(this.mailCredentialsToEdit,this.scope_id).then(
              () => {
                this.isTesting = false;
                this.$emit("isLoading", false);
                resolve({
                  title: this.$t(this.$getConst.pages.alerts.success),
                  body: this.$t(this.$getConst.pages.connectivity_test.smtp_success_message),
                  config: snotifyConfig,
                })
              },
              () => {
                this.isTesting = false;
                this.$emit("isLoading", false);
                reject({
                  title: this.$t(this.$getConst.pages.alerts.error),
                  body: this.$t(this.$getConst.pages.connectivity_test.smtp_failure_message),
                  config: snotifyConfig
                })
              }
            )
          })
        )
      },
      onCancel() {
        this.mailCredentialsToEdit = Object.assign({}, this.mailCredentials);
      },
      onShowPassword() {
        if (this.passwordInputType === "password") {
          this.passwordInputType = "text";
        } else {
          this.passwordInputType = "password";
        }
      },
      onDisplayPassword() {
        this.passwordHidden = !this.passwordHidden;
        this.isActive = !this.isActive;
      },
      loginSMTP() {
        this.isSigningInToSmtp = true;
        this.$emit("isLoading", true);
        this.isLoading = true;

        let currentUri = location.href.replace(location.hash,"");
        currentUri = currentUri.replace("#","");
        location.replace(updateSMTPTokens(this.scope_id, currentUri));
      },
    },
    created() {
      if (this.authType === "basic auth") {
        this.mailCredentialsToEdit = Object.assign({}, this.mailCredentials);
        this.mailCredentialsToEdit.refresh_token = null;
        this.mailCredentialsToEdit.token = null;
      } else if (this.mailCredentials.isOauth) {
        this.isOauth2Connected = true;
        this.mailCredentialsToEdit = Object.assign({}, this.mailCredentials);
      } else {
        this.isOauth2Connected = false;
        this.mailCredentialsToEdit = {
          id: this.mailCredentials.id,
          mail_host: "smtp.office365.com",
          mail_port: 587,
          mail_username: null,
          mail_password: null,
        }
      }
    }
  }
</script>

<style scoped>
  #mail-password-input {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }
  .passwordDisplayed{
    visibility: hidden !important;

  }
  #toggle-password a, #toggle-hidden-password a{
    cursor: pointer;
  }
  #toggle-password {
    border-top-right-radius: 8px !important;
    border-bottom-right-radius: 8px !important;
  }
  #toggle-hidden-password {
    position: relative;
    padding-right: 16px;
    float: left;
  }
  #mail-password{
    margin-bottom: 0.5rem !important;
  }
  .password-right{
    float: right;
    position: relative;
    width: 80%;
  }
  .toggle-icon .input-group-text{
    padding: 0.64rem 1.4rem 0.65rem !important;
  }
  @media screen and (max-width: 880px) {

    .password-right {
      width: 70%;
    }
  }
  @media screen and (max-width: 295px) {

    .password-right{
      float: left;
      width: 98%;
      margin-top: 5%;
    }
  }
</style>
